import { queryClient } from '@/lib/vue-query'
import type { SupportedCurrency } from '@/types/currency'
import { DEFAULT_CURRENCY, SUPPORTED_CURRENCIES } from '@/types/currency'
import { defineStore } from 'pinia'
import { computed, readonly, ref } from 'vue'

/**
 * Centralized currency management store
 * Single source of truth for currency state across the application
 */
export const useCurrencyStore = defineStore('currency', () => {
  // State
  const selectedCurrency = ref<SupportedCurrency>(DEFAULT_CURRENCY)
  const isInitialized = ref(false)
  const isChangingCurrency = ref(false) // Prevent multiple simultaneous changes

  // Computed properties
  const currencySymbol = computed(() => selectedCurrency.value)
  const isMultiCurrencyEnabled = computed(() => true) // for future expansion
  const supportedCurrencies = computed(() => [...SUPPORTED_CURRENCIES])

  // Actions
  function setCurrency(
    currency: SupportedCurrency,
    options?: {
      skipRouteSync?: boolean
      skipInvalidation?: boolean
    }
  ) {
    if (!SUPPORTED_CURRENCIES.includes(currency)) {
      console.warn(`Unsupported currency: ${currency}. Falling back to ${DEFAULT_CURRENCY}`)
      currency = DEFAULT_CURRENCY
    }

    const oldCurrency = selectedCurrency.value

    if (oldCurrency === currency) {
      return // No change needed
    }

    // Prevent multiple simultaneous currency changes
    if (isChangingCurrency.value) {
      console.debug('Currency change already in progress, skipping')
      return
    }

    isChangingCurrency.value = true

    try {
      selectedCurrency.value = currency

      // Save to localStorage
      try {
        localStorage.setItem('selectedCurrency', currency)
      } catch (error) {
        console.warn('Failed to save currency to localStorage:', error)
      }

      // Update URL if not skipping route sync (i.e., when user manually changes currency)
      if (!options?.skipRouteSync) {
        updateURLWithCurrency(currency)
      }

      // Invalidate all currency-dependent queries unless explicitly skipped
      if (!options?.skipInvalidation) {
        invalidateAllCurrencyQueries(oldCurrency)
      }
    } finally {
      // Reset the flag after a short delay to allow for any pending operations
      setTimeout(() => {
        isChangingCurrency.value = false
      }, 100)
    }
  }

  function updateURLWithCurrency(currency: SupportedCurrency) {
    try {
      // Use dynamic import to avoid circular dependency
      import('vue-router').then(({ useRouter, useRoute }) => {
        const router = useRouter()
        const route = useRoute()
        
        if (route && router) {
          const newQuery = { ...route.query, coin: currency }
          router.replace({ ...route, query: newQuery })
        }
      }).catch(() => {
        // Ignore errors - this is a best-effort update
      })
    } catch (error) {
      console.warn('Failed to update URL with currency:', error)
    }
  }

  function initializeFromStorage() {
    if (isInitialized.value) return

    try {
      const savedCurrency = localStorage.getItem('selectedCurrency') as SupportedCurrency
      if (savedCurrency && SUPPORTED_CURRENCIES.includes(savedCurrency)) {
        selectedCurrency.value = savedCurrency
      }
    } catch (error) {
      console.warn('Failed to load currency from localStorage:', error)
    }

    isInitialized.value = true
  }

  function initializeFromRoute(routeCoin?: string) {
    if (!routeCoin) return

    const currency = routeCoin.toUpperCase() as SupportedCurrency
    if (SUPPORTED_CURRENCIES.includes(currency)) {
      setCurrency(currency, { skipRouteSync: true })
    }
  }

  function invalidateAllCurrencyQueries(oldCurrency: SupportedCurrency) {
    // Remove queries that contain the old currency
    queryClient.removeQueries({
      predicate: (query) => {
        const queryKey = query.queryKey
        if (!Array.isArray(queryKey) || queryKey.length < 2) return false

        const [namespace] = queryKey

        // Check different query key patterns for old currency dependency
        let shouldRemove = false

        // Pattern 1: Direct currency in key (e.g., ['user', 'account-overview', 'BTC'])
        if (queryKey.includes(oldCurrency)) {
          shouldRemove = true
        }

        // Pattern 2: Currency in params object (e.g., ['user', 'workers', { coin: 'BTC' }])
        else if (
          queryKey.some(
            (key) =>
              typeof key === 'object' && key !== null && 'coin' in key && key.coin === oldCurrency
          )
        ) {
          shouldRemove = true
        }

        // Pattern 3: Currency in nested params (e.g., ['wallet', 'payment-history', { coin: 'BTC', ... }])
        else if (
          queryKey.some(
            (key) =>
              typeof key === 'object' &&
              key !== null &&
              typeof key === 'object' &&
              Object.values(key).includes(oldCurrency)
          )
        ) {
          shouldRemove = true
        }

        // Only remove queries from relevant namespaces
        const relevantNamespaces = ['user', 'wallet', 'pool', 'unreg-user', 'worker']
        if (shouldRemove && relevantNamespaces.includes(namespace as string)) {
          return true
        }

        return false
      }
    })
  }

  function reset() {
    selectedCurrency.value = DEFAULT_CURRENCY
    try {
      localStorage.removeItem('selectedCurrency')
    } catch (error) {
      console.warn('Failed to remove currency from localStorage:', error)
    }
  }

  return {
    // State (readonly to prevent direct mutations)
    selectedCurrency: readonly(selectedCurrency),
    isInitialized: readonly(isInitialized),

    // Computed
    currencySymbol,
    isMultiCurrencyEnabled,
    supportedCurrencies,

    // Actions
    setCurrency,
    initializeFromStorage,
    initializeFromRoute,
    reset
  }
})
